import type { HistorySourceTypeConfig } from "./history-source-config";
import {
    PlatformHistoryTwins,
    PlatformHistoryTwinsBySourceType,
    toPlatformTwinSources,
} from "../history-platform-twins";
import { AuditLogChangeType, ResourceType } from "@common/types/auditLog";
import { APIProcessName, SQSProcessName } from "../process-names";
import { HistoryField, HistoryGroupBy } from "./history-fields";

export interface ScheduleActivitySource {
    sourceType: string;
    resourceType:
        | ResourceType.SCHEDULE_TEMPLATE
        | ResourceType.SCHEDULE_APPLICATION;
    changeTypes: readonly AuditLogChangeType[];
    platformTwins: PlatformHistoryTwins;
}

const SCHEDULE_APPLICATION_UPDATE_PLATFORM_TWINS: PlatformHistoryTwinsBySourceType =
    {
        [APIProcessName.SCHEDULE_APPLICATION_CHANGE]: null,
        [APIProcessName.SCHEDULE_APPLICATION_PAUSE]: null,
        [APIProcessName.SCHEDULE_APPLICATION_RESUME]: null,
        [APIProcessName.SCHEDULE_APPLICATION_CANCEL]: null,
        [APIProcessName.SCHEDULE_REVIEW_CONFIRM_RECOGNITION]: null,
        [APIProcessName.SCHEDULE_REVIEW_CONFIRM_ALL_RECOGNITION]: null,
        [SQSProcessName.SCHEDULE_RECOGNITION_AUTO_POST]: null,
    };

// Each source counts only its own change types, so rows that shared
// internals write under the same process (a bulk apply's replaced
// schedules, a one-off policy saved during an apply) stay out of the action.
export const SCHEDULE_ACTIVITY_SOURCES: readonly ScheduleActivitySource[] = [
    {
        sourceType: APIProcessName.SCHEDULE_TEMPLATE_CREATE,
        resourceType: ResourceType.SCHEDULE_TEMPLATE,
        changeTypes: [AuditLogChangeType.CREATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_TEMPLATE_UPDATE,
        resourceType: ResourceType.SCHEDULE_TEMPLATE,
        changeTypes: [AuditLogChangeType.UPDATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_TEMPLATE_ARCHIVE,
        resourceType: ResourceType.SCHEDULE_TEMPLATE,
        changeTypes: [AuditLogChangeType.UPDATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_TEMPLATE_APPLY,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.CREATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_TEMPLATE_APPLY_BULK,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.CREATE],
        platformTwins: null,
    },
    {
        sourceType: SQSProcessName.SCHEDULE_RULE_APPLY,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.CREATE],
        platformTwins: null,
    },
    {
        sourceType: SQSProcessName.SCHEDULE_RULE_SUGGEST,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.CREATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_REVIEW_CONFIRM_DRAFTS,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.UPDATE],
        platformTwins: null,
    },
    {
        sourceType: APIProcessName.SCHEDULE_REVIEW_DISMISS_DRAFTS,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.UPDATE],
        platformTwins: null,
    },
    ...toPlatformTwinSources(SCHEDULE_APPLICATION_UPDATE_PLATFORM_TWINS).map(
        ({ sourceType, platformTwins }): ScheduleActivitySource => ({
            sourceType,
            resourceType: ResourceType.SCHEDULE_APPLICATION,
            changeTypes: [AuditLogChangeType.UPDATE],
            platformTwins,
        }),
    ),
    {
        sourceType: APIProcessName.SCHEDULE_APPLICATION_DELETE,
        resourceType: ResourceType.SCHEDULE_APPLICATION,
        changeTypes: [AuditLogChangeType.UPDATE, AuditLogChangeType.DELETE],
        platformTwins: null,
    },
];

export const SCHEDULE_ACTIVITY_SOURCE_TYPES: readonly string[] =
    SCHEDULE_ACTIVITY_SOURCES.map(({ sourceType }) => sourceType);

export const SCHEDULE_RESOURCE_TYPES: readonly ResourceType[] = [
    ResourceType.SCHEDULE_TEMPLATE,
    ResourceType.SCHEDULE_APPLICATION,
];

const SCHEDULE_FIELDS_BY_RESOURCE_TYPE = {
    [ResourceType.SCHEDULE_TEMPLATE]: [
        HistoryField.NAME,
        HistoryField.DESCRIPTION,
        HistoryField.SCHEDULE_GROUP,
        HistoryField.TERM,
        HistoryField.RECOGNITION,
        HistoryField.HOLDING_ACCOUNT,
        HistoryField.RECOGNITION_ACCOUNT,
        HistoryField.STATUS,
    ],
    [ResourceType.SCHEDULE_APPLICATION]: [
        HistoryField.POLICY,
        HistoryField.SCHEDULE_GROUP,
        HistoryField.SOURCE,
        HistoryField.AMOUNT,
        HistoryField.TERM,
        HistoryField.START_DATE,
        HistoryField.STATUS,
    ],
};

export const scheduleSources: HistorySourceTypeConfig[] =
    SCHEDULE_ACTIVITY_SOURCES.map(
        ({ sourceType, resourceType, platformTwins }) => ({
            sourceType,
            resourceTypeConfigs: [
                {
                    resourceType,
                    fields: SCHEDULE_FIELDS_BY_RESOURCE_TYPE[resourceType],
                },
            ],
            groupBy: HistoryGroupBy.SOURCE_COUNT_DISTINCT,
            platformTwins,
            enabled: true,
        }),
    );
